import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFitPassport } from '@/hooks/useFitPassport';
import { ProfileIcon } from '@/components/nav/navIcons';

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * The account chip + dropdown in the top nav. There's no shopper account
 * system in this app (no login, no server-side session) — so the menu only
 * ever links to things that are real: your FitMap Passport (the closest
 * thing to a "profile"), and the Privacy answer on the Help page. No
 * "Settings" or "Sign out" — nothing exists behind either of those yet, and
 * showing them would imply account functionality that isn't there.
 */
export function UserMenu() {
  const { passport } = useFitPassport();
  const [open, setOpen] = useState(false);
  const hasPassport = !!passport;
  const initials = passport?.displayName ? initialsFor(passport.displayName) : '';

  const triggerLabel = initials
    ? `Profile menu (${passport!.displayName})`
    : hasPassport
      ? 'Profile menu (FitMap Passport set up)'
      : 'Profile menu (set up your FitMap Passport)';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={triggerLabel}
        className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-white py-1 pl-1 pr-2.5 hover:border-brand/40"
      >
        <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-sm font-semibold text-ink">
          {initials || <ProfileIcon className="h-5 w-5" />}
          {hasPassport && !initials && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand" />
          )}
        </span>
        <svg viewBox="0 0 20 20" aria-hidden="true" className="h-4 w-4 text-muted">
          <path d="M5.5 8 10 12.5 14.5 8" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div role="menu" aria-label="User menu" className="absolute right-0 z-20 mt-2 w-48 rounded-2xl border border-stone-200 bg-white p-1.5 shadow-card">
          <Link
            role="menuitem"
            to="/passport"
            onClick={() => setOpen(false)}
            className="block rounded-xl px-3 py-2 text-sm font-medium text-ink hover:bg-stone-50"
          >
            Profile
          </Link>
          <Link
            role="menuitem"
            to="/help#privacy"
            onClick={() => setOpen(false)}
            className="block rounded-xl px-3 py-2 text-sm font-medium text-ink hover:bg-stone-50"
          >
            Privacy
          </Link>
        </div>
      )}
    </div>
  );
}
